import { useState, useEffect, useRef } from "react";
import logo from "../icon/logo.png";
import supabase from "../supabase";
import {
  LayoutDashboard, Copy, Users, TrendingUp, Bot, CandlestickChart,
  CreditCard, UserCircle, Settings, Headset,
  Wallet, BarChart2, Target, Bell, LogOut,
  Plus, ChevronRight, Rocket, Crown, Zap, Leaf,
  CircleCheck, LineChart, PanelLeftClose, PanelLeftOpen
} from "lucide-react";


/* ─── Apply saved appearance settings from localStorage / user_settings ──────── */
function applyAppearance(s = null) {
  try {
    const data = s || JSON.parse(localStorage.getItem('tf_appearance') || '{}');
    if (!data || !Object.keys(data).length) return;
    const root = document.documentElement;

    // Accent colour
    if (data.accent_color || data.accentColor) {
      const hex = (data.accent_color || data.accentColor).replace('#', '');
      const r = parseInt(hex.slice(0,2),16), g = parseInt(hex.slice(2,4),16), b = parseInt(hex.slice(4,6),16);
      const accent = data.accent_color || data.accentColor;
      root.style.setProperty('--accent',      accent);
      root.style.setProperty('--accent-dim',  `rgba(${r},${g},${b},.10)`);
      root.style.setProperty('--accent-glow', `rgba(${r},${g},${b},.05)`);
    }

    // Theme
    const theme = data.theme || 'dark';
    const effective = theme === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : theme;
    if (effective === 'dark') {
      root.style.setProperty('--bg',       '#04050a');
      root.style.setProperty('--surface',  '#090b13');
      root.style.setProperty('--surface2', '#0e1119');
      root.style.setProperty('--surface3', '#131727');
      root.style.setProperty('--border',   '#161a26');
      root.style.setProperty('--border2',  '#232a3d');
      root.style.setProperty('--text',     '#f3f5fb');
      root.style.setProperty('--muted',    '#8a92ab');
      root.style.setProperty('--faint',    '#4a5268');
    } else {
      root.style.setProperty('--bg',       '#f4f6fb');
      root.style.setProperty('--surface',  '#ffffff');
      root.style.setProperty('--surface2', '#eef1f9');
      root.style.setProperty('--surface3', '#e6eaf5');
      root.style.setProperty('--border',   '#e2e6f2');
      root.style.setProperty('--border2',  '#ccd4e8');
      root.style.setProperty('--text',     '#0f1729');
      root.style.setProperty('--muted',    '#647089');
      root.style.setProperty('--faint',    '#a7b0c7');
      root.style.setProperty('--card-shadow', '0 1px 0 rgba(255,255,255,.6) inset, 0 12px 28px -18px rgba(15,23,41,.18)');
    }

    // Density
    const density = data.layout_density || data.density || 'comfortable';
    const mainPad = { compact: '16px 20px 32px', comfortable: '24px 28px 40px', spacious: '32px 36px 56px' };
    document.querySelectorAll('.db-main, .in-main, .sb-main').forEach(el => {
      el.style.padding = mainPad[density] || mainPad.comfortable;
    });
  } catch (_) {}
}

/* ─── Fetch USD → target-currency exchange rate (cached 1hr in localStorage) ─── */
async function getUsdRate(targetCurrency) {
  if (!targetCurrency || targetCurrency === 'USD') return 1;
  const ONE_HOUR = 60 * 60 * 1000;
  const cacheKey = `tf_fx_rate_${targetCurrency}`;
  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
    if (cached && typeof cached.rate === 'number' && Date.now() - cached.ts < ONE_HOUR) {
      return cached.rate;
    }
  } catch (_) {}

  try {
    const res = await fetch('https://open.er-api.com/v6/latest/USD');
    const json = await res.json();
    const rate = Number(json?.rates?.[targetCurrency]);
    if (!rate || Number.isNaN(rate)) return 1; // fall back to 1:1 if the API doesn't have this currency
    try { localStorage.setItem(cacheKey, JSON.stringify({ rate, ts: Date.now() })); } catch (_) {}
    return rate;
  } catch (_) {
    // Network/API failure — fall back to a stale cached rate if we have one, else 1:1
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      if (cached && typeof cached.rate === 'number') return cached.rate;
    } catch (_) {}
    return 1;
  }
}

// ─── Design Tokens & Global CSS ──────────────────────────────────────────────
// Direction: "Institutional cobalt" — crisp white surfaces, a single cobalt-blue
// accent, tabular mono for figures, a restrained serif for the page mark only.
// Layout: icon-rail sidebar (expands on demand) + a bento grid of independently
// sized panels instead of a fixed 3-column dashboard grid.
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { font-size: 14px; }

  :root {
    /* Core palette — charcoal glow */
    --bg:         #04050a;
    --surface:    #090b13;
    --surface2:   #0e1119;
    --surface3:   #131727;
    --border:     #161a26;
    --border2:    #232a3d;
    --card-shadow: 0 1px 0 rgba(255,255,255,.025) inset, 0 28px 56px -30px rgba(0,0,0,.85), 0 2px 6px rgba(0,0,0,.4);

    /* Text */
    --text:       #f3f5fb;
    --muted:      #8a92ab;
    --faint:      #4a5268;

    /* Accent — glowing cobalt */
    --accent:      #5b8cff;
    --accent-dim:  rgba(91,140,255,.14);
    --accent-glow: rgba(91,140,255,.45);
    --on-accent:   #ffffff;

    /* Status */
    --green:      #2be3a0;
    --green-dim:  rgba(43,227,160,.14);
    --red:        #ff5c72;
    --red-dim:    rgba(255,92,114,.14);
    --blue:       #5b8cff;
    --blue-dim:   rgba(91,140,255,.14);
    --purple:     #a98bff;
    --purple-dim: rgba(169,139,255,.14);

    /* Hero balance card — glowing charcoal tile */
    --hero-bg:    linear-gradient(155deg, #101627 0%, #070a13 55%, #04050a 100%);
    --hero-text:  #ffffff;

    /* Layout */
    --rail-w:     72px;
    --rail-w-exp: 228px;
    --topbar-h:   60px;
    --ticker-h:   30px;

    /* Fonts */
    --sans:  'Inter', sans-serif;
    --serif: 'Fraunces', serif;
    --mono:  'IBM Plex Mono', monospace;

    /* Radius */
    --r-sm: 8px;
    --r-md: 10px;
    --r-lg: 14px;
  }

  body, #root {
    font-family: var(--sans);
    background: var(--bg);
    color: var(--text);
    height: 100vh;
    overflow: hidden;
    -webkit-font-smoothing: antialiased;
  }

  a { color: inherit; text-decoration: none; }
  button { font-family: var(--sans); cursor: pointer; border: none; background: none; }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 4px; }

  /* ── Animated gradient mesh backdrop ── */
  .mesh-bg {
    position: fixed; inset: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
  }
  .mesh-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
    opacity: .55;
    will-change: transform;
  }
  .mesh-blob-a {
    width: 520px; height: 520px;
    top: -120px; left: -80px;
    background: radial-gradient(circle, rgba(91,140,255,.55), transparent 70%);
    animation: meshDrift1 26s ease-in-out infinite alternate;
  }
  .mesh-blob-b {
    width: 460px; height: 460px;
    bottom: -140px; right: -60px;
    background: radial-gradient(circle, rgba(169,139,255,.45), transparent 70%);
    animation: meshDrift2 32s ease-in-out infinite alternate;
  }
  .mesh-blob-c {
    width: 420px; height: 420px;
    top: 35%; right: 12%;
    background: radial-gradient(circle, rgba(43,227,160,.30), transparent 70%);
    animation: meshDrift3 38s ease-in-out infinite alternate;
  }
  @keyframes meshDrift1 { from { transform: translate(0,0) scale(1); } to { transform: translate(70px, 50px) scale(1.15); } }
  @keyframes meshDrift2 { from { transform: translate(0,0) scale(1); } to { transform: translate(-60px, -40px) scale(1.1); } }
  @keyframes meshDrift3 { from { transform: translate(0,0) scale(1); } to { transform: translate(-50px, 60px) scale(.92); } }
  @media (prefers-reduced-motion: reduce) {
    .mesh-blob { animation: none; }
  }

  /* ── App shell ── */
  .shell {
    display: grid;
    grid-template-columns: var(--rail-w) 1fr;
    grid-template-rows: 1fr;
    height: 100vh;
    overflow: hidden;
    transition: grid-template-columns .2s cubic-bezier(.4,0,.2,1);
  }
  .shell.rail-expanded { grid-template-columns: var(--rail-w-exp) 1fr; }

  /* ── SIDEBAR (icon rail, expands on demand) ── */
  .sidebar {
    grid-column: 1;
    grid-row: 1;
    background: var(--surface);
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    align-items: center;
    width: var(--rail-w);
    height: 100vh;
    overflow: hidden;
    position: relative;
    z-index: 100;
    transition: width .2s cubic-bezier(.4,0,.2,1);
    flex-shrink: 0;
    padding: 16px 0;
  }
  .sidebar.expanded {
    width: var(--rail-w-exp);
    align-items: stretch;
    padding: 16px 12px;
  }

  .rail-brand {
    width: 40px; height: 40px;
    border-radius: var(--r-md);
    background: var(--accent);
    color: var(--on-accent);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    margin-bottom: 20px;
    overflow: hidden;
  }
  .rail-brand img { width: 24px; height: 24px; object-fit: contain; }
  .sidebar.expanded .rail-brand {
    width: 100%; height: auto;
    justify-content: flex-start;
    background: none; color: var(--text);
    padding: 0 4px;
    gap: 10px;
  }
  .sidebar.expanded .rail-brand img { width: 30px; height: 30px; }
  .rail-brand-name {
    display: none;
    font-family: var(--serif);
    font-size: 16px; font-weight: 600;
    letter-spacing: -.2px;
  }
  .sidebar.expanded .rail-brand-name { display: inline; }

  .rail-toggle {
    position: absolute;
    top: 18px; right: -13px;
    width: 26px; height: 26px;
    border-radius: 50%;
    background: var(--surface);
    border: 1px solid var(--border2);
    display: flex; align-items: center; justify-content: center;
    color: var(--muted);
    cursor: pointer;
    z-index: 10;
    transition: color .15s, border-color .15s;
  }
  .rail-toggle:hover { color: var(--accent); border-color: var(--accent); }
  .rail-toggle svg { width: 14px; height: 14px; }

  .rail-scroll { flex: 1; overflow-y: auto; overflow-x: hidden; width: 100%; }
  .rail-section {
    padding: 14px 0 6px;
    font-size: 9px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 1.3px;
    color: var(--faint);
    text-align: center;
    display: none;
  }
  .sidebar.expanded .rail-section { display: block; text-align: left; padding-left: 8px; }

  .rail-link {
    display: flex; align-items: center;
    gap: 12px;
    width: 100%;
    height: 42px;
    padding: 0 0;
    justify-content: center;
    font-size: 13px; font-weight: 500;
    color: var(--muted);
    border-radius: var(--r-sm);
    transition: all .15s;
    cursor: pointer;
    position: relative;
    margin-bottom: 2px;
  }
  .sidebar.expanded .rail-link { justify-content: flex-start; padding: 0 10px; }
  .rail-link svg { width: 18px; height: 18px; flex-shrink: 0; }
  .rail-link:hover { color: var(--text); background: var(--surface2); }
  .rail-link.active { color: var(--accent); background: var(--accent-dim); box-shadow: inset 0 0 0 1px rgba(91,140,255,.25), 0 0 18px -6px rgba(91,140,255,.6); }

  .rail-link-label { display: none; white-space: nowrap; }
  .sidebar.expanded .rail-link-label { display: inline; }

  .rail-badge {
    margin-left: auto;
    font-size: 8.5px; font-weight: 700;
    background: var(--accent); color: var(--on-accent);
    padding: 2px 6px; border-radius: 5px;
    letter-spacing: .3px;
    display: none;
  }
  .sidebar.expanded .rail-badge { display: inline-block; }

  /* Tooltip for collapsed rail */
  .rail-tip {
    position: absolute; left: calc(100% + 10px); top: 50%;
    transform: translateY(-50%);
    background: var(--text); color: var(--surface);
    font-size: 11px; font-weight: 600;
    padding: 5px 9px; border-radius: 6px;
    white-space: nowrap;
    opacity: 0; pointer-events: none;
    transition: opacity .12s;
    z-index: 50;
  }
  .rail-link:hover .rail-tip { opacity: 1; }
  .sidebar.expanded .rail-tip { display: none; }

  .rail-logout {
    color: var(--red);
    width: 100%; background: none; font-family: var(--sans); text-align: left;
  }
  .rail-logout svg { color: var(--red); }
  .rail-logout:hover { background: var(--red-dim); }

  .rail-user {
    flex-shrink: 0;
    border-top: 1px solid var(--border);
    padding-top: 12px;
    margin-top: 8px;
    display: flex; align-items: center; gap: 10px;
    width: 100%;
    justify-content: center;
  }
  .sidebar.expanded .rail-user { justify-content: flex-start; padding-left: 6px; }
  .rail-avatar {
    width: 34px; height: 34px;
    border-radius: 50%;
    background: var(--accent);
    color: var(--on-accent); font-size: 12px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .rail-user-meta { display: none; overflow: hidden; }
  .sidebar.expanded .rail-user-meta { display: block; }
  .rail-user-name { font-size: 12.5px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .rail-user-role { font-size: 10px; color: var(--accent); margin-top: 1px; }

  /* ── RIGHT PANEL (topbar + content) ── */
  .right-panel {
    grid-column: 2;
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }

  /* ── TOPBAR ── */
  .topbar {
    height: var(--topbar-h);
    flex-shrink: 0;
    display: flex; align-items: center;
    padding: 0 28px;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    gap: 16px;
    z-index: 50;
  }

  .topbar-title {
    font-family: var(--serif);
    font-size: 15px; color: var(--text);
    flex: 1;
  }
  .topbar-title span { color: var(--accent); font-weight: 600; }

  .topbar-icon {
    width: 36px; height: 36px;
    border-radius: var(--r-sm);
    background: var(--surface2);
    border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: all .15s;
    color: var(--muted);
    position: relative;
  }
  .topbar-icon svg { width: 18px; height: 18px; }
  .topbar-icon:hover { border-color: var(--accent); color: var(--accent); }
  .topbar-icon .notif-dot {
    position: absolute; top: 6px; right: 6px;
    width: 6px; height: 6px;
    background: var(--red); border-radius: 50%;
    border: 1.5px solid var(--surface);
  }

  .profile-menu { position: relative; }
  .topbar-avatar {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: var(--accent);
    color: var(--on-accent); font-size: 12px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    border: none;
    box-shadow: 0 0 16px -4px rgba(91,140,255,.8);
    flex-shrink: 0;
  }
  .profile-dropdown {
    position: absolute; top: calc(100% + 10px); right: 0;
    background: var(--surface); border: 1px solid var(--border2);
    border-radius: var(--r-md); padding: 6px;
    min-width: 190px; z-index: 400;
    box-shadow: 0 16px 40px rgba(15,23,41,.14);
    animation: fadeSlide .18s ease both;
  }
  .profile-dropdown-header {
    padding: 8px 10px 10px;
    border-bottom: 1px solid var(--border);
    margin-bottom: 6px;
  }
  .profile-dropdown-name {
    font-size: 12px; font-weight: 700; color: var(--text);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .profile-dropdown-email {
    font-size: 10px; color: var(--muted); margin-top: 2px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .profile-dropdown-item {
    display: flex; align-items: center; gap: 9px;
    padding: 8px 10px; border-radius: var(--r-sm);
    font-size: 12px; font-weight: 500; color: var(--muted);
    cursor: pointer; font-family: var(--sans);
    transition: background .12s, color .12s;
  }
  .profile-dropdown-item svg { width: 15px; height: 15px; }
  .profile-dropdown-item:hover { background: var(--surface2); color: var(--text); }
  .profile-dropdown-item.logout { color: var(--red); }
  .profile-dropdown-item.logout:hover { background: var(--red-dim); color: var(--red); }

  /* ── Language switcher (topbar) ── */
  .lang-switcher { position: relative; display: inline-flex; align-items: center; }
  .lang-btn {
    display: inline-flex; align-items: center; gap: 5px;
    background: var(--surface2); border: 1px solid var(--border);
    border-radius: var(--r-sm); padding: 5px 9px;
    font-size: 11px; font-weight: 600; color: var(--muted);
    cursor: pointer; font-family: var(--sans);
    transition: border-color .15s, color .15s; white-space: nowrap;
    height: 36px;
  }
  .lang-btn:hover { border-color: var(--accent); color: var(--text); }
  .lang-dropdown {
    position: absolute; top: calc(100% + 6px); right: 0;
    background: var(--surface); border: 1px solid var(--border2);
    border-radius: var(--r-md); padding: 6px;
    min-width: 176px; z-index: 400;
    box-shadow: 0 16px 40px rgba(15,23,41,.14);
    animation: fadeSlide .18s ease both;
  }
  @keyframes fadeSlide {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .lang-option {
    display: flex; align-items: center; gap: 9px;
    padding: 8px 10px; border-radius: var(--r-sm);
    font-size: 12px; color: var(--muted);
    cursor: pointer; font-family: var(--sans);
    transition: background .12s, color .12s;
  }
  .lang-option:hover { background: var(--surface2); color: var(--text); }
  .lang-option.active { color: var(--accent); background: var(--accent-dim); font-weight: 600; }
  .lang-section-label {
    font-size: 9px; font-weight: 700; color: var(--faint);
    letter-spacing: .1em; text-transform: uppercase;
    padding: 4px 10px 6px;
  }

  /* Hide Google's default toolbar */
  .goog-te-banner-frame, #goog-gt-tt, .goog-te-balloon-frame { display: none !important; }
  body { top: 0 !important; }
  .skiptranslate { display: none !important; }

  /* Hamburger (mobile) */
  .topbar-hamburger {
    display: none;
    flex-direction: column; gap: 5px; cursor: pointer; padding: 4px;
  }
  .topbar-hamburger span {
    display: block; width: 20px; height: 2px;
    background: var(--text); border-radius: 2px; transition: all .2s;
  }

  /* ── TICKER ── */
  .ticker {
    height: var(--ticker-h);
    flex-shrink: 0;
    background: var(--surface2);
    border-bottom: 1px solid var(--border);
    overflow: hidden;
    display: flex; align-items: center;
  }
  .ticker-track {
    display: flex; white-space: nowrap;
    animation: scroll-ticker 50s linear infinite;
    will-change: transform;
  }
  @keyframes scroll-ticker {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  .ticker-item {
    font-family: var(--mono);
    font-size: 10.5px;
    padding: 0 20px;
    border-right: 1px solid var(--border);
    line-height: var(--ticker-h);
    display: inline-flex; align-items: center; gap: 8px;
  }
  .t-sym { color: var(--muted); }
  .t-up  { color: var(--green); }
  .t-dn  { color: var(--red); }

  /* ── MAIN CONTENT ── */
  .main {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 24px 28px 40px;
  }

  /* Page header */
  .page-header {
    display: flex; align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 22px;
  }
  .page-greeting {
    font-family: var(--serif);
    font-size: clamp(20px, 3vw, 26px); font-weight: 600;
    color: var(--text);
    line-height: 1.2;
  }
  .page-greeting em { color: var(--accent); font-style: normal; }
  .page-sub {
    font-size: 13px; color: var(--muted);
    margin-top: 4px;
    display: flex; align-items: center; gap: 8px;
  }
  .live-dot {
    width: 6px; height: 6px;
    background: var(--green);
    border-radius: 50%;
    box-shadow: 0 0 8px var(--green);
    animation: pulse 2s infinite;
    flex-shrink: 0;
  }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.3} }

  /* Buttons */
  .btn {
    display: inline-flex; align-items: center; justify-content: center;
    gap: 7px; border-radius: var(--r-sm);
    font-family: var(--sans); font-weight: 600;
    cursor: pointer; transition: all .15s;
    border: none; text-decoration: none; white-space: nowrap;
  }
  .btn-sm { font-size: 12px; padding: 7px 14px; }
  .btn-md { font-size: 13px; padding: 9px 18px; }
  .btn-accent { background: var(--accent); color: var(--on-accent); box-shadow: 0 0 22px -6px rgba(91,140,255,.7); }
  .btn-accent:hover { background: #4577f0; box-shadow: 0 0 28px -4px rgba(91,140,255,.9); }
  .btn-ghost {
    background: var(--surface);
    border: 1px solid var(--border);
    color: var(--text); font-size: 12px; padding: 7px 14px;
  }
  .btn-ghost:hover { border-color: var(--accent); color: var(--accent); }

  /* ── BENTO GRID ── */
  .bento {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
    align-items: stretch;
  }
  .cell-2 { grid-column: span 2; }
  .cell-4 { grid-column: span 4; }
  .cell-1 { grid-column: span 1; }

  /* ── CARD ── */
  .card {
    background:
      linear-gradient(180deg, rgba(255,255,255,.025) 0%, rgba(255,255,255,0) 22%),
      var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    padding: 20px;
    transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
    position: relative;
    overflow: hidden;
    height: 100%;
    box-shadow: var(--card-shadow);
  }
  .card::after {
    content: '';
    position: absolute; inset: 0;
    border-radius: inherit;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,.02);
    pointer-events: none;
  }
  .card:hover {
    border-color: var(--border2);
    box-shadow: 0 1px 0 rgba(255,255,255,.03) inset, 0 32px 64px -28px rgba(0,0,0,.9), 0 0 0 1px rgba(91,140,255,.06);
    transform: translateY(-1px);
  }

  .card-title {
    font-size: 11px; font-weight: 700;
    color: var(--muted);
    text-transform: uppercase; letter-spacing: 1px;
    margin-bottom: 16px;
  }
  .card-header {
    display: flex; align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  .card-header .card-title { margin-bottom: 0; }
  .card-link {
    font-size: 11px; color: var(--accent);
    font-weight: 600; cursor: pointer;
    display: flex; align-items: center; gap: 4px;
    transition: opacity .15s, transform .15s;
  }
  .card-link:hover { opacity: .75; transform: translateX(1px); }

  /* ── HERO (portfolio value) ── */
  .hero-card {
    background: var(--hero-bg);
    border: 1px solid rgba(91,140,255,.28);
    box-shadow: 0 0 0 1px rgba(91,140,255,.05), 0 26px 60px -26px rgba(91,140,255,.55), inset 0 1px 0 rgba(255,255,255,.05);
    color: var(--hero-text);
    display: flex; flex-direction: column; justify-content: space-between;
    position: relative;
    overflow: hidden;
  }
  .hero-card::before {
    content: '';
    position: absolute;
    top: -60%; right: -25%;
    width: 280px; height: 280px;
    background: radial-gradient(circle, rgba(91,140,255,.32), transparent 70%);
    filter: blur(10px);
    pointer-events: none;
  }
  .hero-label, .hero-value, .hero-sub { position: relative; z-index: 1; }
  .hero-label {
    font-size: 10px; font-weight: 700; letter-spacing: 1.3px; text-transform: uppercase;
    color: rgba(255,255,255,.55);
    display: flex; align-items: center; gap: 6px;
    margin-bottom: 14px;
  }
  .hero-value {
    font-family: var(--mono);
    font-size: clamp(26px, 4vw, 38px); font-weight: 600;
    letter-spacing: -1px;
    line-height: 1;
    word-break: break-word;
  }
  .hero-sub {
    margin-top: 10px;
    font-size: 12.5px;
    color: rgba(255,255,255,.65);
    display: flex; align-items: center; gap: 6px;
  }
  .hero-sub .up { color: #6fe3a8; }
  .hero-sub .dn { color: #ff8e86; }

  /* ── STAT CLUSTER (small tiles inside a cell) ── */
  .stat-cluster {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    height: 100%;
  }
  .stat-tile {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    padding: 16px 18px;
    display: flex; flex-direction: column; justify-content: space-between;
    transition: border-color .15s ease;
    position: relative;
    overflow: hidden;
  }
  .stat-tile::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 2px;
    background: var(--stat-accent, var(--border2));
    opacity: .8;
  }
  .stat-tile:hover { border-color: var(--border2); }
  .stat-icon {
    width: 28px; height: 28px;
    border-radius: 7px;
    background: var(--surface2);
    border: 1px solid var(--border2);
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 12px;
  }
  .stat-icon svg { width: 14px; height: 14px; }
  .stat-label {
    font-size: 9.5px; font-weight: 700;
    color: var(--muted);
    text-transform: uppercase; letter-spacing: .8px;
    margin-bottom: 6px;
  }
  .stat-value {
    font-family: var(--mono);
    font-size: 19px; font-weight: 600;
    color: var(--text);
    line-height: 1;
    letter-spacing: -.3px;
  }
  .stat-value.green { color: var(--green); }
  .stat-value.red   { color: var(--red); }
  .stat-badge {
    font-size: 10.5px; color: var(--muted);
    margin-top: 10px; padding-top: 10px;
    border-top: 1px solid var(--border);
  }
  .badge-up { color: var(--green); }
  .badge-dn { color: var(--red); }

  /* ── PERFORMANCE CHART ── */
  .perf-headline {
    font-family: var(--mono);
    font-size: 30px; font-weight: 600; color: var(--text);
    letter-spacing: -1px; margin-bottom: 2px;
  }
  .perf-headline.up { color: var(--green); }
  .perf-headline.dn { color: var(--red); }
  .perf-sub {
    font-size: 12px; color: var(--muted); margin-bottom: 18px;
  }
  .perf-sub span { color: var(--text); font-weight: 600; }

  .perf-grid-bg {
    background-image:
      linear-gradient(var(--border) 1px, transparent 1px),
      linear-gradient(90deg, var(--border) 1px, transparent 1px);
    background-size: 100% 25%, 8.33% 100%;
    border-radius: var(--r-md);
    padding: 10px 6px 0;
    margin: 0 -6px;
  }

  .perf-chart { display: block; width: 100%; height: 112px; overflow: visible; }
  .perf-chart-pt {
    cursor: pointer;
    transition: r .15s;
  }
  .perf-chart-pt:hover { r: 5.5; }
  .bar-labels {
    display: flex; justify-content: space-between;
    margin-top: 8px;
    font-family: var(--mono); font-size: 9px; color: var(--muted);
  }

  /* Segment tabs */
  .seg-tabs {
    display: flex; gap: 2px;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--r-sm);
    padding: 3px;
    margin-bottom: 16px;
  }
  .seg-tab {
    flex: 1; padding: 5px;
    text-align: center;
    font-size: 11px; font-weight: 600;
    color: var(--muted);
    border-radius: 6px; cursor: pointer;
    transition: all .15s;
  }
  .seg-tab.active { background: var(--surface); color: var(--accent); box-shadow: 0 1px 3px rgba(15,23,41,.1); }

  /* ── POSITIONS TABLE ── */
  .dt { width: 100%; border-collapse: collapse; font-size: 12px; }
  .dt th {
    text-align: left; padding: 9px 10px;
    color: var(--faint); font-size: 9.5px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 1px;
    border-bottom: 1px solid var(--border);
    background: var(--surface3);
  }
  .dt th:first-child { border-radius: var(--r-sm) 0 0 0; }
  .dt th:last-child  { border-radius: 0 var(--r-sm) 0 0; }
  .dt td {
    padding: 12px 10px;
    border-bottom: 1px solid var(--border);
    color: var(--text);
    vertical-align: middle;
  }
  .dt tr:last-child td { border-bottom: none; }
  .dt tr { transition: background .15s ease; }
  .dt tr:hover td { background: rgba(91,140,255,.035); }

  .asset-cell { display: flex; align-items: center; gap: 9px; }
  .asset-icon {
    width: 28px; height: 28px;
    border-radius: 7px;
    background: linear-gradient(155deg, var(--surface3), var(--surface2));
    border: 1px solid var(--border2);
    box-shadow: inset 0 1px 0 rgba(255,255,255,.04);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700;
    color: var(--accent);
    font-family: var(--mono);
    flex-shrink: 0;
  }
  .asset-name { font-weight: 600; font-family: var(--mono); font-size: 12px; }
  .asset-pair { font-size: 10px; color: var(--muted); margin-top: 1px; }

  .mono-val { font-family: var(--mono); }
  .up  { color: var(--green); }
  .dn  { color: var(--red); }

  .badge {
    display: inline-flex; align-items: center;
    padding: 3px 9px; border-radius: 6px;
    font-size: 10px; font-weight: 700;
    letter-spacing: .2px;
    border: 1px solid transparent;
  }
  .badge-buy   { background: var(--green-dim); color: var(--green); border-color: rgba(43,227,160,.25); }
  .badge-sell  { background: var(--red-dim);   color: var(--red);   border-color: rgba(255,92,114,.25); }
  .badge-open  { background: var(--accent-dim);color: var(--accent);border-color: rgba(91,140,255,.28); }
  .badge-closed{ background: var(--surface3);  color: var(--muted); border-color: var(--border2); }
  .badge-green { background: var(--green-dim); color: var(--green); border-color: rgba(43,227,160,.25); }
  .badge-red   { background: var(--red-dim);   color: var(--red);   border-color: rgba(255,92,114,.25); }
  .badge-gold  { background: var(--accent-dim);color: var(--accent);border-color: rgba(91,140,255,.28); }
  .badge-blue  { background: var(--blue-dim);  color: var(--blue);  border-color: rgba(91,140,255,.28); }

  .progress-bar {
    height: 5px; background: var(--surface3);
    border-radius: 99px; overflow: hidden;
    box-shadow: inset 0 1px 2px rgba(0,0,0,.4);
  }
  .progress-fill {
    height: 100%; background: var(--accent);
    border-radius: 99px; transition: width .4s ease;
    box-shadow: 0 0 10px -1px var(--accent-glow);
  }

  /* Asset Allocation — radial rings */
  .alloc-radial-wrap {
    display: flex; align-items: center; gap: 20px; flex-wrap: wrap;
  }
  .alloc-rings { flex-shrink: 0; position: relative; width: 132px; height: 132px; }
  .alloc-rings svg { width: 100%; height: 100%; transform: rotate(-90deg); }
  .alloc-ring-bg { fill: none; stroke: var(--surface2); }
  .alloc-ring-seg {
    fill: none; stroke-linecap: round;
    transition: stroke-dashoffset .6s ease;
  }
  .alloc-ring-center {
    position: absolute; inset: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center;
  }
  .alloc-ring-center-lbl { font-size: 8.5px; color: var(--muted); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px; }
  .alloc-ring-center-val { font-family: var(--mono); font-size: 15px; font-weight: 700; color: var(--text); }

  .alloc-legend { flex: 1; display: flex; flex-direction: column; gap: 11px; min-width: 0; }
  .alloc-legend-row { display: flex; align-items: center; gap: 9px; font-size: 12px; }
  .alloc-legend-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .alloc-legend-label { flex: 1; color: var(--text); }
  .alloc-legend-pct { font-family: var(--mono); font-weight: 600; color: var(--muted); }

  .alloc-totals {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 10px; margin-top: 16px;
  }
  .alloc-tot-card {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--r-sm);
    padding: 10px 12px;
  }
  .alloc-tot-lbl { font-size: 9.5px; color: var(--muted); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
  .alloc-tot-val { font-family: var(--mono); font-size: 14px; font-weight: 600; color: var(--text); }
  .alloc-tot-val.accent { color: var(--accent); }

  /* Copy trading */
  .copy-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
  .copy-count { font-family: var(--mono); font-size: 15px; font-weight: 600; }
  .copy-alloc { font-size: 11px; color: var(--muted); margin-top: 2px; }
  .copy-row {
    display: flex; align-items: center; gap: 11px;
    padding: 9px 10px; margin: 0 -10px 4px;
    border-radius: var(--r-sm);
    transition: background .15s ease;
  }
  .copy-row:hover { background: var(--surface2); }
  .copy-av {
    width: 32px; height: 32px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: #fff;
    flex-shrink: 0;
    box-shadow: 0 0 0 1px var(--surface), 0 0 0 2px var(--border2), 0 4px 10px -2px rgba(0,0,0,.6);
  }
  .copy-name { font-size: 12px; font-weight: 600; }
  .copy-pct {
    font-family: var(--mono); font-size: 11.5px; font-weight: 700; margin-left: auto;
    background: var(--green-dim); color: var(--green);
    border: 1px solid rgba(43,227,160,.25);
    padding: 3px 8px; border-radius: 6px;
  }

  /* Quick actions */
  .quick-list { display: flex; flex-direction: column; gap: 7px; }
  .quick-btn {
    display: flex; align-items: center; gap: 12px;
    padding: 11px 14px;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--r-sm);
    color: var(--text); font-size: 13px; font-weight: 500;
    cursor: pointer; transition: all .15s ease;
    text-decoration: none;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.02);
  }
  .quick-btn svg {
    color: var(--accent); width: 16px; height: 16px;
    flex-shrink: 0; padding: 6px; box-sizing: content-box;
    background: var(--accent-dim); border-radius: 7px;
    margin: -6px 0;
  }
  .quick-btn:hover {
    border-color: rgba(91,140,255,.4); color: var(--accent);
    background: var(--surface3);
    box-shadow: 0 6px 18px -8px rgba(91,140,255,.35), inset 0 1px 0 rgba(255,255,255,.02);
    transform: translateX(2px);
  }
  .quick-btn-arrow { margin-left: auto; color: var(--faint); width: 15px; height: 15px; transition: transform .15s ease; }
  .quick-btn:hover .quick-btn-arrow { transform: translateX(2px); color: var(--accent); }

  /* Marketplace */
  .mp-grid { display: flex; flex-direction: column; gap: 8px; }
  .mp-row {
    display: flex; align-items: center; gap: 12px;
    padding: 10px 12px;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--r-sm);
    transition: border-color .15s ease, background .15s ease, transform .15s ease;
  }
  .mp-row:hover { border-color: rgba(91,140,255,.35); background: var(--surface3); transform: translateX(2px); }
  .mp-icon {
    width: 36px; height: 36px; border-radius: 9px;
    display: flex; align-items: center; justify-content: center;
    color: #fff; flex-shrink: 0;
    box-shadow: 0 6px 14px -4px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.12);
  }
  .mp-icon svg { width: 17px; height: 17px; }
  .mp-name { font-size: 13px; font-weight: 600; }
  .mp-tag  { font-size: 10px; color: var(--muted); margin-top: 2px; }
  .mp-roi  { font-family: var(--mono); font-size: 12px; font-weight: 700; text-align: right; color: var(--green); }
  .mp-price{ font-size: 10px; color: var(--muted); margin-top: 2px; text-align: right; }

  /* NEW badge */
  .new-badge {
    font-size: 8.5px; font-weight: 800;
    background: var(--accent); color: var(--on-accent);
    padding: 2px 6px; border-radius: 4px;
    letter-spacing: .5px;
    box-shadow: 0 0 12px -2px var(--accent-glow);
  }

  /* Plan card (now a bento tile) */
  .plan-card {
    display: flex; flex-direction: column;
    background:
      radial-gradient(120% 140% at 100% 0%, color-mix(in srgb, var(--plan-badge-color, var(--accent)) 10%, transparent), transparent 60%),
      linear-gradient(180deg, rgba(255,255,255,.025) 0%, rgba(255,255,255,0) 22%),
      var(--surface);
  }
  .plan-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
  .plan-badge {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 10px; font-weight: 700; letter-spacing: .6px;
    text-transform: uppercase;
    padding: 4px 9px; border-radius: 20px;
    background: var(--plan-badge-bg, var(--accent-dim));
    color: var(--plan-badge-color, var(--accent));
    border: 1px solid color-mix(in srgb, var(--plan-badge-color, var(--accent)) 35%, transparent);
  }
  .plan-badge svg { width: 11px; height: 11px; }
  .plan-icon {
    width: 20px; height: 20px;
    color: var(--plan-badge-color, var(--accent));
    opacity: .8;
    filter: drop-shadow(0 0 6px color-mix(in srgb, var(--plan-badge-color, var(--accent)) 55%, transparent));
  }
  .plan-label { font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
  .plan-sub { font-size: 11px; color: var(--muted); line-height: 1.4; }
  .plan-bar-wrap { margin: 14px 0 10px; height: 5px; border-radius: 2px; background: var(--surface3); overflow: hidden; box-shadow: inset 0 1px 2px rgba(0,0,0,.5); }
  .plan-bar-fill {
    height: 100%; border-radius: 2px;
    background: var(--plan-badge-color, var(--accent));
    box-shadow: 0 0 10px -1px var(--plan-badge-color, var(--accent-glow));
    transition: width .6s ease;
  }
  .plan-upgrade {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    width: 100%; padding: 10px 0; border-radius: var(--r-sm);
    font-size: 12px; font-weight: 700;
    background: var(--plan-btn-bg, var(--accent));
    color: var(--plan-btn-color, var(--on-accent));
    margin-top: auto;
    box-shadow: 0 8px 20px -8px color-mix(in srgb, var(--plan-btn-bg, var(--accent)) 70%, transparent);
    transition: opacity .15s, transform .15s;
  }
  .plan-upgrade:hover { opacity: .92; transform: translateY(-1px); }
  .plan-upgrade svg { width: 13px; height: 13px; }
  .plan-current {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    width: 100%; padding: 10px 0; border-radius: var(--r-sm);
    font-size: 12px; font-weight: 600;
    background: var(--surface2);
    border: 1px solid var(--border2);
    color: var(--muted); margin-top: auto;
  }
  .plan-current svg { width: 13px; height: 13px; color: var(--green); }

  /* Divider */
  .divider { height: 1px; background: var(--border); border: none; margin: 14px 0; }

  /* ── MOBILE OVERLAY ── */
  .sb-backdrop {
    display: none;
    position: fixed; inset: 0;
    background: rgba(15,23,41,.5);
    z-index: 99;
  }
  .sb-backdrop.open { display: block; }

  /* ── Bottom nav (mobile) ── */
  .bottom-nav {
    display: none;
    position: fixed; bottom: 0; left: 0; right: 0;
    height: 60px;
    background: var(--surface);
    border-top: 1px solid var(--border);
    z-index: 200;
    align-items: center;
    justify-content: space-around;
    padding: 0 8px;
  }
  .bn-item {
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    color: var(--muted); font-size: 9px; font-weight: 700;
    text-transform: uppercase; letter-spacing: .5px;
    cursor: pointer; padding: 4px 10px;
    border-radius: 8px; transition: color .15s; text-decoration: none;
  }
  .bn-item svg { width: 20px; height: 20px; }
  .bn-item.active { color: var(--accent); }

  /* ── RESPONSIVE ── */
  @media (max-width: 1300px) {
    .main { padding: 20px 22px 32px; }
  }

  @media (max-width: 1100px) {
    .cell-2 { grid-column: span 4; }
    .stat-cluster { grid-template-columns: 1fr 1fr; }
  }

  @media (max-width: 900px) {
    .topbar { padding: 0 20px; }
    .page-header { flex-wrap: wrap; gap: 10px; }
  }

  @media (max-width: 768px) {
    .shell, .shell.rail-expanded { grid-template-columns: 1fr !important; }
    .sidebar {
      position: fixed; top: 0; left: 0; height: 100vh;
      transform: translateX(-100%); z-index: 300;
      width: var(--rail-w-exp); align-items: stretch; padding: 16px 12px;
      box-shadow: 4px 0 32px rgba(15,23,41,.18);
      transition: transform .2s cubic-bezier(.4,0,.2,1);
    }
    .sidebar.mobile-open { transform: translateX(0); }
    .sidebar .rail-brand { width: 100%; height: auto; justify-content: flex-start; background: none; color: var(--text); padding: 0 4px; gap: 10px; }
    .sidebar .rail-brand-name, .sidebar .rail-section, .sidebar .rail-link-label, .sidebar .rail-badge, .sidebar .rail-user-meta { display: block; }
    .sidebar .rail-link { justify-content: flex-start; padding: 0 10px; }
    .sidebar .rail-user { justify-content: flex-start; padding-left: 6px; }
    .rail-toggle { display: none; }
    .right-panel { grid-column: 1; }
    .topbar-hamburger { display: flex; }
    .topbar-rail-toggle { display: none; }
    .cell-1, .cell-2, .cell-4 { grid-column: span 4; }
    .stat-cluster { grid-template-columns: 1fr 1fr; }
    .bottom-nav { display: flex; }
    .main { padding-bottom: 80px; }
  }

  @media (max-width: 600px) {
    .stat-cluster { gap: 10px; }
    .stat-value { font-size: 16px; }
    .hero-value { font-size: 30px; }
    .page-greeting { font-size: 21px; }
    .topbar { padding: 0 16px; }
    .main { padding: 16px 16px 80px; }
  }

  @media (max-width: 380px) {
    .stat-cluster { grid-template-columns: 1fr; }
  }

  /* Keep content from over-stretching on very large / ultrawide screens */
  @media (min-width: 1800px) {
    .page-header, .bento {
      max-width: 1500px;
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

// ─── Static Data ──────────────────────────────────────────────────────────────
const BAR_HEIGHTS = [35, 48, 42, 62, 28, 70, 66, 80, 74, 88, 95, 100];
const BAR_MONTHS  = ['Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr','May'];

/* ─── FIFO buy/sell matching for stock_orders ─────────────────────────────────
 * Given all filled stock_orders for a user (any symbol), match each sell against
 * the oldest unmatched buys for that symbol (FIFO) to compute realized P&L per
 * closed lot, plus how many shares (if any) remain open per symbol.
 * Returns: { realizedPnl, wins, losses, openLots }
 *   - realizedPnl: total realized P&L across all matched sell lots
 *   - wins / losses: count of matched sell lots that were profitable / not
 *   - openLots: number of symbols still holding a net positive quantity (i.e. open stock positions)
 */
function computeStockFifo(orders) {
  const bySymbol = {};
  for (const o of orders) {
    if (o.status !== 'filled') continue;
    const sym = o.symbol;
    if (!bySymbol[sym]) bySymbol[sym] = [];
    bySymbol[sym].push(o);
  }

  let realizedPnl = 0;
  let wins = 0;
  let losses = 0;
  let openLots = 0;
  const openPositions = [];

  for (const sym of Object.keys(bySymbol)) {
    const rows = [...bySymbol[sym]].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const buyQueue = [];

    for (const row of rows) {
      const price = Number(row.filled_price ?? row.limit_price ?? 0);
      let qty = Number(row.quantity ?? 0);

      if (row.side === 'buy') {
        buyQueue.push({ qty, price });
      } else if (row.side === 'sell') {
        let lotPnl = 0;
        while (qty > 0 && buyQueue.length) {
          const lot = buyQueue[0];
          const matched = Math.min(lot.qty, qty);
          lotPnl += matched * (price - lot.price);
          lot.qty -= matched;
          qty -= matched;
          if (lot.qty <= 0) buyQueue.shift();
        }
        realizedPnl += lotPnl;
        if (lotPnl > 0) wins += 1;
        else if (lotPnl < 0) losses += 1;
      }
    }

    const remaining = buyQueue.reduce((s, l) => s + l.qty, 0);
    if (remaining > 0.0000001) {
      openLots += 1;
      const costBasis = buyQueue.reduce((s, l) => s + l.qty * l.price, 0);
      openPositions.push({
        symbol:   sym,
        quantity: remaining,
        avgCost:  costBasis / remaining,
        costBasis,
        // Most-recent fill for this symbol, used as a stand-in "last price" reference
        lastPrice: Number(rows[rows.length - 1]?.filled_price ?? rows[rows.length - 1]?.limit_price ?? 0),
        openedAt: rows.find(r => r.side === 'buy')?.created_at ?? rows[0]?.created_at,
      });
    }
  }

  return { realizedPnl, wins, losses, openLots, openPositions };
}

const TICKER_ITEMS = [
  { sym:'BTC/USD', val:'$67,420', pct:'+2.3%', up:true },
  { sym:'ETH/USD', val:'$3,840',  pct:'+1.8%', up:true },
  { sym:'S&P 500', val:'5,248',   pct:'-0.4%', up:false },
  { sym:'GOLD',    val:'$2,314',  pct:'+0.9%', up:true },
  { sym:'NVDA',    val:'$892',    pct:'+3.1%', up:true },
  { sym:'SOL/USD', val:'$168',    pct:'+4.7%', up:true },
  { sym:'BNB/USD', val:'$602',    pct:'+1.2%', up:true },
  { sym:'XRP/USD', val:'$0.58',   pct:'+0.8%', up:true },
  { sym:'EUR/USD', val:'1.0862',  pct:'-0.1%', up:false },
  { sym:'OIL',     val:'$82.40',  pct:'+0.5%', up:true },
];

const ALLOCATIONS = [
  { label:'Crypto',  pct:42, color:'var(--accent)' },
  { label:'Forex',   pct:31, color:'var(--green)'  },
  { label:'Stocks',  pct:18, color:'var(--blue)'   },
  { label:'Cash',    pct:9,  color:'var(--faint)'  },
];

const COPY_TRADERS = [
  { initials:'VF', name:'vForce',  pct:'+38.4%', prog:62, bg:'var(--accent)' },
  { initials:'SM', name:'sofiaM',  pct:'+24.7%', prog:40, bg:'var(--blue)' },
  { initials:'RK', name:'RaptorK', pct:'+19.1%', prog:30, bg:'var(--purple)' },
];

const MP_ITEMS = [
  { Icon:Bot,       name:'AlphaGrid',  tag:'Grid Bot',       roi:'+41.2%', price:'$29/mo', color:'var(--accent)' },
  { Icon:Bot,       name:'MomentumX',  tag:'Trend Bot',      roi:'+28.7%', price:'$19/mo', color:'var(--blue)' },
  { Icon:TrendingUp,name:'CryptoEdge', tag:'Signal Service',  roi:'+55.1%', price:'$39/mo', color:'var(--purple)' },
];

const MAIN_LINKS = [
  { href:'/dashboard',    Icon:LayoutDashboard, label:'Dashboard',    active:true },
  { href:'/copy-trading', Icon:Copy,            label:'Copy Trading'              },
  { href:'/hire-trader',  Icon:Users,           label:'Hire a Trader'             },
  { href:'/insights',     Icon:TrendingUp,      label:'Insights'                  },
  { href:'/market-place', Icon:Bot,             label:'Marketplace',  badge:'NEW' },
  { href:'/terminal',     Icon:CandlestickChart,label:'Terminal'                  },
  { href:'/stocks',       Icon:LineChart,       label:'Stocks',    },
];
const ACCOUNT_LINKS = [
  { href:'/payments', Icon:CreditCard, label:'Payments' },
  { href:'/profile',  Icon:UserCircle, label:'Profile'  },
  { href:'/settings', Icon:Settings,   label:'Settings' },
  { href:'/support',  Icon:Headset,    label:'Support'  },
];

// ─── Language Switcher ───────────────────────────────────────────────────────
const LANGUAGES = [
  { code:"en",    label:"English",   flag:"🇬🇧" },
  { code:"fr",    label:"Français",  flag:"🇫🇷" },
  { code:"es",    label:"Español",   flag:"🇪🇸" },
  { code:"de",    label:"Deutsch",   flag:"🇩🇪" },
  { code:"pt",    label:"Português", flag:"🇧🇷" },
  { code:"ar",    label:"العربية",   flag:"🇸🇦" },
  { code:"zh-CN", label:"中文",      flag:"🇨🇳" },
  { code:"ja",    label:"日本語",    flag:"🇯🇵" },
  { code:"ru",    label:"Русский",   flag:"🇷🇺" },
  { code:"hi",    label:"हिन्दी",    flag:"🇮🇳" },
  { code:"yo",    label:"Yoruba",    flag:"🇳🇬" },
  { code:"ha",    label:"Hausa",     flag:"🇳🇬" },
  { code:"af",    label:"Afrikaans", flag:"🇿🇦" },
  { code:"pl",    label:"Polski",    flag:"🇵🇱" },
  { code:"fi",    label:"Suomi",     flag:"🇫🇮" },
  { code:"cs",    label:"Čeština",   flag:"🇨🇿" },
  { code:"ga",    label:"Gaeilge",   flag:"🇮🇪" },
  { code:"it",    label:"Italiano",  flag:"🇮🇹" },
  { code:"nl",    label:"Nederlands",flag:"🇳🇱" },
  { code:"sv",    label:"Svenska",   flag:"🇸🇪" },
  { code:"no",    label:"Norsk",     flag:"🇳🇴" },
  { code:"da",    label:"Dansk",     flag:"🇩🇰" },
  { code:"el",    label:"Ελληνικά",  flag:"🇬🇷" },
  { code:"uk",    label:"Українська",flag:"🇺🇦" },
  { code:"tr",    label:"Türkçe",    flag:"🇹🇷" },
  { code:"ko",    label:"한국어",     flag:"🇰🇷" },
  { code:"vi",    label:"Tiếng Việt",flag:"🇻🇳" },
  { code:"id",    label:"Bahasa Indonesia", flag:"🇮🇩" },
  { code:"th",    label:"ไทย",       flag:"🇹🇭" },
  { code:"sw",    label:"Kiswahili", flag:"🇰🇪" },
  { code:"he",    label:"עברית",     flag:"🇮🇱" },
  { code:"ur",    label:"اردو",      flag:"🇵🇰" },
  { code:"fa",    label:"فارسی",     flag:"🇮🇷" },
  { code:"bn",    label:"বাংলা",     flag:"🇧🇩" },
];

const initGoogleTranslate = () => {
  if (window._gtInitDone) return;
  window._gtInitDone = true;
  if (!document.getElementById("google_translate_element")) {
    const el = document.createElement("div");
    el.id = "google_translate_element";
    el.style.cssText = "display:none!important;position:absolute;left:-9999px;";
    document.body.appendChild(el);
  }
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement({ pageLanguage:"en", autoDisplay:false }, "google_translate_element");
    const saved = localStorage.getItem("tf_lang");
    if (saved && saved !== "en") setTimeout(() => applyGoogleTranslate(saved), 800);
  };
  const script = document.createElement("script");
  script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.head.appendChild(script);
};

const applyGoogleTranslate = (langCode) => {
  if (langCode === "en") {
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname;
    window.location.reload();
    return;
  }
  const pair = "/en/" + langCode;
  document.cookie = "googtrans=" + pair + "; path=/";
  document.cookie = "googtrans=" + pair + "; path=/; domain=" + window.location.hostname;
  const sel = document.querySelector(".goog-te-combo");
  if (sel) { sel.value = langCode; sel.dispatchEvent(new Event("change")); }
  else window.location.reload();
};

function LanguageSwitcher() {
  const [open, setOpen]       = useState(false);
  const [current, setCurrent] = useState(() => localStorage.getItem("tf_lang") || "en");
  const ref                   = useRef(null);

  useEffect(() => { initGoogleTranslate(); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const select = (code) => {
    setCurrent(code);
    localStorage.setItem("tf_lang", code);
    setOpen(false);
    applyGoogleTranslate(code);
  };

  const active = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];

  return (
    <div className="lang-switcher" ref={ref}>
      <button className="lang-btn" onClick={() => setOpen(o => !o)} title="Change language">
        <span style={{fontSize:"13px"}}>{active.flag}</span>
        <span style={{fontSize:"9px", opacity:.5}}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="lang-dropdown">
          <div className="lang-section-label">Language</div>
          {LANGUAGES.map(l => (
            <div
              key={l.code}
              className={"lang-option" + (l.code === current ? " active" : "")}
              onClick={() => select(l.code)}
            >
              <span style={{fontSize:"15px"}}>{l.flag}</span>
              <span>{l.label}</span>
              {l.code === current && <span style={{marginLeft:"auto", fontSize:"10px"}}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const PLAN_META = {
  free:  { label:'Free',  Icon:Leaf,   badgeBg:'var(--surface2)',        badgeColor:'var(--muted)',  btnBg:'var(--accent)', btnColor:'var(--on-accent)', fill:20,  sub:'Limited access · 2 positions', upgrade:true  },
  pro:   { label:'Pro',   Icon:Zap,    badgeBg:'var(--accent-dim)',      badgeColor:'var(--accent)', btnBg:'var(--accent)', btnColor:'var(--on-accent)', fill:60,  sub:'Full access · Unlimited positions', upgrade:true  },
  elite: { label:'Elite', Icon:Crown,  badgeBg:'var(--purple-dim)',      badgeColor:'var(--purple)', btnBg:'var(--purple-dim)', btnColor:'var(--purple)', fill:100, sub:'Everything · Priority support', upgrade:false },
};

function PlanCard({ plan }) {
  const key  = (plan ?? 'free').toLowerCase();
  const meta = PLAN_META[key] || PLAN_META.free;

  const style = {
    '--plan-badge-bg':     meta.badgeBg,
    '--plan-badge-color':  meta.badgeColor,
    '--plan-btn-bg':       meta.btnBg,
    '--plan-btn-color':    meta.btnColor,
  };

  return (
    <div className="card plan-card" style={style}>
      <div className="card-title">Plan</div>
      <div className="plan-row">
        <span className="plan-badge">
          <meta.Icon />
          {meta.label} Plan
        </span>
        <meta.Icon className="plan-icon" />
      </div>
      <div className="plan-label">
        {key === 'elite' ? "You're on the top tier" : key === 'pro' ? 'Pro features active' : 'Upgrade your plan'}
      </div>
      <div className="plan-sub">{meta.sub}</div>
      <div className="plan-bar-wrap">
        <div className="plan-bar-fill" style={{ width: `${meta.fill}%` }} />
      </div>
      {meta.upgrade ? (
        <a href="/payments" style={{ marginTop:'auto' }}>
          <button className="plan-upgrade">
            <Rocket />
            {key === 'pro' ? 'Upgrade to Elite' : 'Upgrade to Pro'}
          </button>
        </a>
      ) : (
        <div className="plan-current">
          <CircleCheck />
          Highest plan · You're all set
        </div>
      )}
    </div>
  );
}

function Sidebar({ open, expanded, onToggleExpand, onClose, user, onLogout }) {
  const initials    = user ? `${user.first_name?.[0]??''}${user.last_name?.[0]??''}`.toUpperCase() || user.email?.[0]?.toUpperCase() || '?' : '?';
  const displayName = user ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email : '—';
  const planLabel   = user?.plan ? `${user.plan.charAt(0).toUpperCase()}${user.plan.slice(1)} · ${user.is_verified ? 'Verified' : 'Unverified'}` : '—';

  return (
    <aside className={`sidebar${expanded ? ' expanded' : ''}${open ? ' mobile-open' : ''}`}>
      <button type="button" className="rail-toggle" onClick={onToggleExpand} title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}>
        {expanded ? <PanelLeftClose /> : <PanelLeftOpen />}
      </button>

      <div className="rail-brand">
        <img src={logo} alt="vantrexmarket" />
        <span className="rail-brand-name">vantrexmarket</span>
      </div>

      <div className="rail-scroll">
        <div className="rail-section">Main</div>
        {MAIN_LINKS.map(l => (
          <a key={l.label} href={l.href} className={`rail-link${l.active ? ' active' : ''}`} onClick={onClose}>
            <l.Icon />
            <span className="rail-link-label">{l.label}</span>
            {l.badge && <span className="rail-badge">{l.badge}</span>}
            <span className="rail-tip">{l.label}</span>
          </a>
        ))}
        <div className="rail-section">Account</div>
        {ACCOUNT_LINKS.map(l => (
          <a key={l.label} href={l.href} className="rail-link" onClick={onClose}>
            <l.Icon />
            <span className="rail-link-label">{l.label}</span>
            <span className="rail-tip">{l.label}</span>
          </a>
        ))}
        <button type="button" className="rail-link rail-logout" onClick={onLogout}>
          <LogOut />
          <span className="rail-link-label">Logout</span>
          <span className="rail-tip">Logout</span>
        </button>
      </div>

      <div className="rail-user">
        <div className="rail-avatar">{initials}</div>
        <div className="rail-user-meta">
          <div className="rail-user-name">{displayName}</div>
          <div className="rail-user-role">{planLabel}</div>
        </div>
      </div>
    </aside>
  );
}

function Topbar({ onMenu, user, onLogout, railExpanded, onToggleRail }) {
  const firstName = user?.first_name || user?.email?.split('@')[0] || 'Trader';
  const initials  = user ? `${user.first_name?.[0]??''}${user.last_name?.[0]??''}`.toUpperCase() || user.email?.[0]?.toUpperCase() || '?' : '?';
  const displayName = user ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email : '—';

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="topbar">
      <div className="topbar-hamburger" onClick={onMenu}>
        <span /><span /><span />
      </div>
      <button
        type="button"
        className="topbar-icon topbar-rail-toggle"
        onClick={onToggleRail}
        title={railExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        {railExpanded ? <PanelLeftClose /> : <PanelLeftOpen />}
      </button>
      <div className="topbar-title">Hi, <span>{firstName}</span></div>
      <LanguageSwitcher />
      <div className="topbar-icon">
        <a href="/notification">
        <Bell />
        <span className="notif-dot" /></a>
      </div>
      <div className="topbar-icon"><a href="/settings"><Settings /></a></div>

      <div className="profile-menu" ref={menuRef}>
        <button type="button" className="topbar-avatar" onClick={() => setMenuOpen(o => !o)} title="Account">
          {initials}
        </button>
        {menuOpen && (
          <div className="profile-dropdown">
            <div className="profile-dropdown-header">
              <div className="profile-dropdown-name">{displayName}</div>
              {user?.email && <div className="profile-dropdown-email">{user.email}</div>}
            </div>
            <a href="/profile" className="profile-dropdown-item">
              <UserCircle />
              Profile
            </a>
            <div className="profile-dropdown-item logout" onClick={onLogout}>
              <LogOut />
              Logout
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Ticker() {
  const items = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div className="ticker">
      <div className="ticker-track">
        {items.map((t, i) => (
          <span key={i} className="ticker-item">
            <span className="t-sym">{t.sym}</span>
            <span>{t.val}</span>
            <span className={t.up ? 't-up' : 't-dn'}>{t.pct}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function HeroCard({ snapshot, currSymbol = '$' }) {
  const fmt = n => `${currSymbol}${Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 })}`;
  const value = snapshot ? fmt(snapshot.total_value) : `${currSymbol}0.00`;
  const pnl = snapshot ? Number(snapshot.total_pnl) : 0;
  const pnlPct = snapshot ? Number(snapshot.total_pnl_pct) : 0;
  const dir = pnl >= 0 ? 'up' : 'dn';

  return (
    <div className="card hero-card">
      <div className="hero-label">
        <span className="live-dot" style={{ width:6, height:6, background: pnl >= 0 ? '#6fe3a8' : '#ff8e86' }} />
        Live portfolio value
      </div>
      <div className="hero-value">{value}</div>
      <div className="hero-sub">
        <span className={dir}>{pnl >= 0 ? '▲' : '▼'} {fmt(Math.abs(pnl))}</span>
        <span>({pnl >= 0 ? '+' : ''}{pnlPct.toFixed(1)}% all-time)</span>
      </div>
    </div>
  );
}

function StatCluster({ snapshot, currSymbol = '$' }) {
  const fmt = n => `${currSymbol}${Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits:0, maximumFractionDigits:0 })}`;

  const totalPnl    = snapshot ? `${Number(snapshot.total_pnl) >= 0 ? '+' : ''}${fmt(snapshot.total_pnl)}` : `+${currSymbol}0`;
  const openPos     = snapshot ? String(snapshot.open_positions) : '0';
  const winRate     = snapshot ? `${Number(snapshot.win_rate_pct).toFixed(0)}%` : '0%';
  const cryptoPct   = snapshot ? Math.round(snapshot.alloc_crypto_pct) : 0;
  const forexPct    = snapshot ? Math.round(snapshot.alloc_forex_pct)  : 0;
  const pnlPositive = snapshot && Number(snapshot.total_pnl) >= 0;

  const cards = [
    { Icon:TrendingUp,  iconBg:pnlPositive ? 'var(--green-dim)' : 'var(--red-dim)', iconColor:pnlPositive ? 'var(--green)' : 'var(--red)',
      label:'Total P&L',      value:totalPnl, cls:pnlPositive ? 'green' : 'red',
      badge:pnlPositive ? 'Up all-time' : 'Down all-time', badgeCls:pnlPositive ? 'badge-up' : 'badge-dn' },
    { Icon:BarChart2,   iconBg:'var(--blue-dim)', iconColor:'var(--blue)',
      label:'Open Positions', value:openPos, cls:'',
      badge:'Across all markets', badgeCls:'' },
    { Icon:Target,      iconBg:'var(--purple-dim)', iconColor:'var(--purple)',
      label:'Win Rate',       value:winRate, cls:'',
      badge:'Closed trades', badgeCls:'' },
    { Icon:Wallet,      iconBg:'var(--accent-dim)', iconColor:'var(--accent)',
      label:'Exposure',       value:`${cryptoPct}/${forexPct}`, cls:'',
      badge:'Crypto : Forex split', badgeCls:'' },
  ];

  return (
    <div className="stat-cluster">
      {cards.map(c => (
        <div key={c.label} className="stat-tile" style={{ '--stat-accent': c.iconColor }}>
          <div>
            <div className="stat-icon" style={{ background:c.iconBg, color:c.iconColor }}>
              <c.Icon />
            </div>
            <div className="stat-label">{c.label}</div>
            <div className={`stat-value ${c.cls}`}>{c.value}</div>
          </div>
          <div className="stat-badge">
            <span className={c.badgeCls}>{c.badge}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function PerformanceCard({ activeBar, onBar, perfBars, snapshot }) {
  const [period, setPeriod] = useState('12M');
  const bars   = perfBars?.length ? perfBars : BAR_HEIGHTS.map((h,i) => ({ height_pct:h, is_negative:i===4, period_label:BAR_MONTHS[i] }));
  const labels = bars.map(b => b.period_label);
  const pnlVal = snapshot ? Number(snapshot.total_pnl_pct) : 0;
  const pnlPct = `${pnlVal >= 0 ? '+' : ''}${pnlVal.toFixed(1)}%`;
  const lineColor = pnlVal >= 0 ? 'var(--green)' : 'var(--red)';

  // ── Build a smooth SVG area/line path from the bar data ──
  const W = 640, H = 112, PAD = 6;
  const values = bars.map(b => Number(b.is_negative ? -b.height_pct : b.height_pct));
  const min = Math.min(...values, 0), max = Math.max(...values, 0);
  const span = (max - min) || 1;
  const xStep = bars.length > 1 ? (W - PAD * 2) / (bars.length - 1) : 0;
  const points = values.map((v, i) => ({
    x: PAD + i * xStep,
    y: PAD + (1 - (v - min) / span) * (H - PAD * 2),
  }));
  const zeroY = PAD + (1 - (0 - min) / span) * (H - PAD * 2);

  const smoothLine = (pts) => {
    if (pts.length < 2) return '';
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i], p1 = pts[i + 1];
      const mx = (p0.x + p1.x) / 2;
      d += ` C ${mx} ${p0.y}, ${mx} ${p1.y}, ${p1.x} ${p1.y}`;
    }
    return d;
  };

  const linePath = smoothLine(points);
  const areaPath = `${linePath} L ${points[points.length - 1]?.x ?? 0} ${H - PAD} L ${points[0]?.x ?? 0} ${H - PAD} Z`;

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Performance</span>
        <div className="seg-tabs" style={{ marginBottom:0 }}>
          {['1M','3M','6M','12M'].map(p => (
            <div key={p} className={`seg-tab${period===p?' active':''}`} onClick={()=>setPeriod(p)}>{p}</div>
          ))}
        </div>
      </div>
      <div className={`perf-headline ${pnlVal >= 0 ? 'up' : 'dn'}`}>{pnlPct}</div>
      <div className="perf-sub">vs S&amp;P 500 <span>+9.1%</span> — last 12 months</div>
      <div className="perf-grid-bg">
        <svg className="perf-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="perfAreaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={lineColor} stopOpacity=".38" />
              <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
            </linearGradient>
          </defs>
          <line x1={PAD} y1={zeroY} x2={W - PAD} y2={zeroY} stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3" />
          <path d={areaPath} fill="url(#perfAreaFill)" stroke="none" />
          {/* soft glow pass behind the crisp line */}
          <path d={linePath} fill="none" stroke={lineColor} strokeWidth="6" strokeOpacity=".35" style={{ filter:'blur(4px)' }} />
          <path d={linePath} fill="none" stroke={lineColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {points.map((p, i) => (
            <circle
              key={i}
              className="perf-chart-pt"
              cx={p.x} cy={p.y}
              r={i === activeBar ? 5 : 3.5}
              fill={i === activeBar ? lineColor : 'var(--surface)'}
              stroke={lineColor}
              strokeWidth="2"
              style={i === activeBar ? { filter:`drop-shadow(0 0 5px ${lineColor})` } : undefined}
              onClick={() => onBar(i)}
            >
              <title>{labels[i]}</title>
            </circle>
          ))}
        </svg>
      </div>
      <div className="bar-labels">
        {labels.map(m => <span key={m}>{m}</span>)}
      </div>
    </div>
  );
}

function AllocationCard({ snapshot }) {
  const fmt = n => `$${Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits:0, maximumFractionDigits:0 })}`;

  const allocations = snapshot ? [
    { label:'Crypto', pct: Math.round(snapshot.alloc_crypto_pct ?? 0), color:'var(--accent)' },
    { label:'Forex',  pct: Math.round(snapshot.alloc_forex_pct  ?? 0), color:'var(--green)'  },
    { label:'Stocks', pct: Math.round(snapshot.alloc_stocks_pct ?? 0), color:'var(--blue)'    },
    { label:'Cash',   pct: Math.round(snapshot.alloc_cash_pct   ?? 0), color:'var(--faint)'   },
  ] : ALLOCATIONS.map(a => ({ ...a, pct: 0, color:'var(--faint)' }));

  const invested = fmt(snapshot?.invested ?? 0);
  const cash     = fmt(snapshot?.cash     ?? 0);

  // Concentric rings: outermost = first allocation row, each ring sized down by RING_GAP
  const SIZE = 132, STROKE = 11, RING_GAP = 15;
  const center = SIZE / 2;
  const rings = allocations.map((a, i) => {
    const r = center - STROKE / 2 - i * RING_GAP;
    const circumference = 2 * Math.PI * r;
    const dash = (Math.max(a.pct, 0) / 100) * circumference;
    return { ...a, r, circumference, dash };
  });

  const totalAllocated = allocations.reduce((s, a) => s + a.pct, 0);

  return (
    <div className="card">
      <div className="card-title">Asset Allocation</div>
      <div className="alloc-radial-wrap">
        <div className="alloc-rings">
          <svg viewBox={`0 0 ${SIZE} ${SIZE}`}>
            {rings.map(r => (
              <circle key={`bg-${r.label}`} className="alloc-ring-bg" cx={center} cy={center} r={r.r} strokeWidth={STROKE} />
            ))}
            {rings.map(r => (
              <circle
                key={`seg-${r.label}`}
                className="alloc-ring-seg"
                cx={center} cy={center} r={r.r}
                strokeWidth={STROKE}
                stroke={r.color}
                strokeDasharray={r.circumference}
                strokeDashoffset={r.circumference - r.dash}
                style={{ filter: `drop-shadow(0 0 5px ${r.color === 'var(--faint)' ? 'transparent' : r.color})` }}
              />
            ))}
          </svg>
          <div className="alloc-ring-center">
            <div className="alloc-ring-center-lbl">Allocated</div>
            <div className="alloc-ring-center-val">{totalAllocated}%</div>
          </div>
        </div>
        <div className="alloc-legend">
          {allocations.map(a => (
            <div key={a.label} className="alloc-legend-row">
              <span className="alloc-legend-dot" style={{ background:a.color, boxShadow:a.color === 'var(--faint)' ? 'none' : `0 0 6px ${a.color}` }} />
              <span className="alloc-legend-label">{a.label}</span>
              <span className="alloc-legend-pct">{a.pct}%</span>
            </div>
          ))}
        </div>
      </div>
      <div className="alloc-totals">
        <div className="alloc-tot-card">
          <div className="alloc-tot-lbl">Invested</div>
          <div className="alloc-tot-val accent">{invested}</div>
        </div>
        <div className="alloc-tot-card">
          <div className="alloc-tot-lbl">Cash</div>
          <div className="alloc-tot-val">{cash}</div>
        </div>
      </div>
    </div>
  );
}

function PositionsCard({ positions, currSymbol = '$' }) {
  const display = positions?.length ? positions.map(p => {
    const pl     = Number(p.profit_loss ?? 0);
    const plDir  = pl >= 0 ? 'up' : 'dn';
    const plSign = pl >= 0 ? '+' : '-';
    const pct    = Number(p.open_price) > 0
      ? (((Number(p.current_price) - Number(p.open_price)) / Number(p.open_price)) * 100).toFixed(2)
      : '0.00';
    return {
      pair:    p.symbol,
      sym:     p.symbol.slice(0,2),
      type:    p.trade_type,
      typeC:   p.trade_type === 'BUY' ? 'buy' : 'sell',
      entry:   Number(p.open_price).toFixed(4),
      current: Number(p.current_price).toFixed(4),
      pl:      `${plSign}${currSymbol}${Math.abs(pl).toFixed(2)}`,
      pct:     `${pl >= 0 ? '+' : ''}${pct}%`,
      plDir,
      status:  p.status,
      stC:     p.status === 'open' ? 'open' : 'closed',
    };
  }) : [];

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Open Positions</span>
        <span className="card-link">View all <ChevronRight size={13} /></span>
      </div>
      <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
        <table className="dt">
          <thead>
            <tr>
              <th>Asset</th><th>Type</th><th>Entry</th>
              <th>Current</th><th>P&amp;L</th><th>%</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {display.length === 0
              ? <tr><td colSpan={7} style={{ textAlign:'center', color:'var(--muted)', padding:'24px 0', fontFamily:'var(--mono)', fontSize:12 }}>No open positions</td></tr>
              : display.map(p => (
                <tr key={p.pair}>
                  <td>
                    <div className="asset-cell">
                      <div className="asset-icon">{p.sym.slice(0,2)}</div>
                      <div><div className="asset-name">{p.pair}</div></div>
                    </div>
                  </td>
                  <td><span className={`badge badge-${p.typeC}`}>{p.type}</span></td>
                  <td className="mono-val">{p.entry}</td>
                  <td className="mono-val">{p.current}</td>
                  <td className={`mono-val ${p.plDir}`} style={{ fontWeight:700 }}>{p.pl}</td>
                  <td className={p.plDir} style={{ fontFamily:'var(--mono)', fontWeight:600 }}>{p.pct}</td>
                  <td><span className={`badge badge-${p.stC}`}>{p.status}</span></td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StockPositionsCard({ positions, currSymbol = '$' }) {
  const display = positions?.length ? positions.map(p => {
    const qty = Number(p.quantity ?? 0);
    return {
      symbol:    p.symbol,
      sym:       (p.symbol || '').slice(0,2),
      qty:       qty.toLocaleString('en-US', { maximumFractionDigits: 4 }),
      avgCost:   `${currSymbol}${Number(p.avgCost ?? 0).toFixed(2)}`,
      costBasis: `${currSymbol}${Number(p.costBasis ?? 0).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 })}`,
      opened:    p.openedAt ? new Date(p.openedAt).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '—',
    };
  }) : [];

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Open Stock Positions</span>
        <span className="card-link">View all <ChevronRight size={13} /></span>
      </div>
      <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
        <table className="dt">
          <thead>
            <tr>
              <th>Asset</th><th>Quantity</th><th>Avg. Cost</th>
              <th>Cost Basis</th><th>Opened</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {display.length === 0
              ? <tr><td colSpan={6} style={{ textAlign:'center', color:'var(--muted)', padding:'24px 0', fontFamily:'var(--mono)', fontSize:12 }}>No open stock positions</td></tr>
              : display.map(p => (
                <tr key={p.symbol}>
                  <td>
                    <div className="asset-cell">
                      <div className="asset-icon">{p.sym}</div>
                      <div><div className="asset-name">{p.symbol}</div></div>
                    </div>
                  </td>
                  <td className="mono-val">{p.qty}</td>
                  <td className="mono-val">{p.avgCost}</td>
                  <td className="mono-val" style={{ fontWeight:700 }}>{p.costBasis}</td>
                  <td className="mono-val" style={{ color:'var(--muted)', fontSize:11.5 }}>{p.opened}</td>
                  <td><span className="badge badge-open">open</span></td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MarketplaceCard({ mpItems }) {
  const items = mpItems?.length ? mpItems.map(item => ({
    Icon:  Bot,
    name:  item.name,
    tag:   item.tag          || '',
    roi:   item.roi_12m_pct != null ? `+${Number(item.roi_12m_pct).toFixed(1)}%` : '—',
    price: item.price_monthly != null ? `$${Number(item.price_monthly).toFixed(0)}/mo` : 'Free',
    color: item.color_hex    || 'var(--accent)',
  })) : MP_ITEMS;

  return (
    <div className="card">
      <div className="card-header">
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span className="card-title">Bot &amp; Signal Marketplace</span>
          <span className="new-badge">NEW</span>
        </div>
        <span className="card-link">Browse all <ChevronRight size={13} /></span>
      </div>
      <div className="mp-grid">
        {items.map(item => (
          <div key={item.name} className="mp-row">
            <div className="mp-icon" style={{ background:item.color }}>
              <item.Icon />
            </div>
            <div style={{ flex:1 }}>
              <div className="mp-name">{item.name}</div>
              <div className="mp-tag">{item.tag}</div>
            </div>
            <div>
              <div className="mp-roi">{item.roi}</div>
              <div className="mp-price">{item.price}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop:14 }}>
        <a href="/market-place" className="btn btn-accent btn-sm" style={{ width:'100%', justifyContent:'center' }}>
          <Rocket size={14} /> Explore Marketplace
        </a>
      </div>
    </div>
  );
}

function CopyTradingCard({ copyRels }) {
  const fmt = n => `$${Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits:0, maximumFractionDigits:0 })}`;

  const traders = copyRels?.length ? copyRels.map(r => ({
    initials: r.trader_profiles?.initials || r.trader_profiles?.display_name?.slice(0,2).toUpperCase() || '??',
    name:     r.trader_profiles?.display_name || r.trader_profiles?.handle || '—',
    pct:      r.trader_profiles?.trader_performance?.[0]?.roi_pct != null
                ? `+${Number(r.trader_profiles.trader_performance[0].roi_pct).toFixed(1)}%`
                : '0.0%',
    prog:     Math.min(Math.max(Number(r.trader_profiles?.trader_performance?.[0]?.roi_pct ?? 0), 0), 100),
    bg:       r.trader_profiles?.color_hex || 'var(--accent)',
  })) : [];

  const totalAllocated = copyRels?.length
    ? fmt(copyRels.reduce((s, r) => s + Number(r.allocated_amount ?? 0), 0))
    : '$0';

  return (
    <div className="card">
      <div className="copy-head">
        <div>
          <div className="copy-count">{traders.length} Active {traders.length === 1 ? 'Copy' : 'Copies'}</div>
          <div className="copy-alloc">{totalAllocated} allocated</div>
        </div>
        <a href="/copy-trading" className="btn btn-ghost btn-sm">Manage</a>
      </div>
      <div className="card-title">Copy Trading</div>
      {traders.length === 0
        ? <div style={{ fontSize:12, color:'var(--muted)', fontFamily:'var(--mono)', textAlign:'center', padding:'12px 0' }}>No active copy trades</div>
        : traders.map(t => (
          <div key={t.name} className="copy-row">
            <div className="copy-av" style={{ background:t.bg }}>{t.initials}</div>
            <div style={{ flex:1 }}>
              <div className="copy-name">{t.name}</div>
              <div className="progress-bar" style={{ marginTop:5 }}>
                <div className="progress-fill" style={{ width:`${t.prog}%`, background:t.bg }} />
              </div>
            </div>
            <span className="copy-pct up">{t.pct}</span>
          </div>
        ))
      }
    </div>
  );
}

function QuickActionsCard() {
  return (
    <div className="card">
      <div className="card-title">Quick Actions</div>
      <div className="quick-list">
        {[
          { href:'/copy-trading', Icon:Copy,      label:'Start Copy Trading' },
          { href:'/hire-trader', Icon:Users,      label:'Hire a Trader'      },
          { href:'/payments', Icon:Plus,          label:'Deposit Funds'      },
          { href:'/insights', Icon:BarChart2,     label:'View Insights'      },
        ].map(a => (
          <a key={a.label} href={a.href} className="quick-btn">
            <a.Icon />
            {a.label}
            <ChevronRight className="quick-btn-arrow" />
          </a>
        ))}
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [activeBar,   setActiveBar]   = useState(11);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [railExpanded, setRailExpanded] = useState(false);
  const [user,        setUser]        = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [snapshot,    setSnapshot]    = useState(null);
  const [perfBars,    setPerfBars]    = useState([]);
  const [positions,   setPositions]   = useState([]);
  const [stockPositions, setStockPositions] = useState([]);
  const [copyRels,    setCopyRels]    = useState([]);
  const [mpItems,     setMpItems]     = useState([]);

  // Apply cached appearance immediately on mount (before DB responds)
  useEffect(() => { applyAppearance(); }, []);

  // Inject Chatway live-chat widget
  useEffect(() => {
    if (document.getElementById('chatway-widget-script')) return;
    const script = document.createElement('script');
    script.id = 'chatway-widget-script';
    script.src = 'https://cdn.chatway.app/widget.js?id=WY7avTiTzJy0';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    let mounted = true;

    const bootstrap = async (session) => {
      if (!session) { window.location.href = '/login'; return; }
      const uid = session.user.id;

      const [
        { data: profile, error: profileError },
        { data: bars },
        { data: allPositions },
        { data: stockOrders },
        { data: copies },
        { data: mp },
        { data: userSettings },
      ] = await Promise.all([
        supabase.from('users').select('first_name,last_name,email,plan,is_verified,currency').eq('id', uid).single(),
        supabase.from('performance_bars').select('period_label,height_pct,is_negative').eq('user_id', uid).order('period_start', { ascending:true }).limit(12),
        supabase.from('open_positions').select('symbol,trade_type,open_price,current_price,profit_loss,status,opened_at,closed_at').eq('user_id', uid).order('opened_at', { ascending:false }),
        supabase.from('stock_orders').select('symbol,side,order_type,quantity,limit_price,filled_price,status,created_at').eq('user_id', uid).eq('status','filled').order('created_at', { ascending:true }),
        supabase.from('copy_relationships').select(`allocated_amount,trader_profiles(display_name,handle,initials,color_hex,trader_performance(roi_pct,period))`).eq('copier_id', uid).eq('status','active'),
        supabase.from('marketplace_items').select('name,tag,icon_class,color_hex,price_monthly,roi_12m_pct').eq('is_active', true).order('subscriber_count', { ascending:false }).limit(3),
        supabase.from('user_settings').select('theme,accent_color,layout_density').eq('user_id', uid).maybeSingle(),
      ]);

      if (profileError) {
        console.error('Dashboard: failed to load users row (plan will fall back to Free):', profileError);
      }

      if (!mounted) return;

      const currency = profile?.currency ?? 'USD';

      // Full currency → symbol map (matches the CURRENCIES list in settings)
      const CURRENCY_SYMBOLS = {
        USD:'$', EUR:'€', GBP:'£', JPY:'¥', CHF:'Fr', CAD:'CA$', AUD:'A$',
        NZD:'NZ$', CNY:'¥', HKD:'HK$', SGD:'S$', SEK:'kr', NOK:'kr', DKK:'kr',
        MXN:'$', BRL:'R$', ZAR:'R', NGN:'₦', KES:'KSh', GHS:'₵', EGP:'E£',
        INR:'₹', KRW:'₩', IDR:'Rp', THB:'฿', MYR:'RM', PHP:'₱', TWD:'NT$',
        PKR:'₨', BDT:'৳', VND:'₫', AED:'د.إ', SAR:'﷼', QAR:'﷼', KWD:'د.ك',
        BHD:'.د.ب', OMR:'﷼', JOD:'JD', TRY:'₺', PLN:'zł', CZK:'Kč', HUF:'Ft',
        RON:'lei', BGN:'лв', HRK:'kn', RUB:'₽', UAH:'₴', ILS:'₪', ARS:'$',
        CLP:'$', COP:'$', PEN:'S/', UYU:'$U', VEF:'Bs', DZD:'دج', MAD:'د.م.',
        TND:'DT', LYD:'LD', AOA:'Kz', ETB:'Br', TZS:'TSh', UGX:'USh', ZMW:'ZK',
        MWK:'MK', BTC:'₿', ETH:'Ξ',
      };
      const currSymbol = CURRENCY_SYMBOLS[currency] ?? currency;

      const fmtCurr = (n, decimals = 2) =>
        `${currSymbol}${Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

      // Fetch live wallet balance (wallets are stored in USD)
      const { data: wallets } = await supabase
        .from('wallets')
        .select('balance, currency')
        .eq('user_id', uid);

      const totalWalletBalanceUsd = wallets?.reduce((sum, w) => sum + Number(w?.balance ?? 0), 0) ?? 0;

      // Convert USD → the user's selected currency (live rate, 1hr cached)
      const fxRate = await getUsdRate(currency);
      const totalWalletBalance = totalWalletBalanceUsd * fxRate;

      // ── Live metrics computation ────────────────────────────────────────
      const positionRows = allPositions ?? [];
      const openTrades   = positionRows.filter(p => p.status === 'open').map(p => ({
        ...p,
        profit_loss: Number(p.profit_loss ?? 0) * fxRate, // convert P&L to selected currency too
      }));
      const closedTrades = positionRows.filter(p => p.status === 'closed');

      const tradesPnl   = closedTrades.reduce((s, p) => s + Number(p.profit_loss ?? 0), 0) * fxRate;
      const tradeWins   = closedTrades.filter(p => Number(p.profit_loss ?? 0) > 0).length;
      const tradeLosses = closedTrades.filter(p => Number(p.profit_loss ?? 0) < 0).length;

      const { realizedPnl: stockPnlUsd, wins: stockWins, losses: stockLosses, openLots: openStockLots, openPositions: openStockPositionsRaw }
        = computeStockFifo(stockOrders ?? []);
      const stockPnl = stockPnlUsd * fxRate;

      const openStockPositions = openStockPositionsRaw.map(p => ({
        ...p,
        avgCost:    p.avgCost * fxRate,
        costBasis:  p.costBasis * fxRate,
        lastPrice:  p.lastPrice * fxRate,
      }));

      const totalPnl       = tradesPnl + stockPnl;
      const totalWins      = tradeWins + stockWins;
      const totalLosses    = tradeLosses + stockLosses;
      const totalDecided   = totalWins + totalLosses;
      const winRatePct     = totalDecided > 0 ? (totalWins / totalDecided) * 100 : 0;

      const openPositionsCount = openTrades.length + openStockLots;

      const cryptoSymbols = new Set(['BTC','ETH','BTCUSD','ETHUSD','BTCUSDT','ETHUSDT','LTC','XRP','SOL','DOGE']);
      const isCrypto = (sym) => cryptoSymbols.has((sym ?? '').toUpperCase().replace(/USD[T]?$/,'').replace(/[^A-Z]/g,'')) || cryptoSymbols.has((sym ?? '').toUpperCase());
      const cryptoCount     = openTrades.filter(p => isCrypto(p.symbol)).length;
      const allocCryptoPct  = openTrades.length ? Math.round((cryptoCount / openTrades.length) * 100) : 0;
      const allocForexPct   = openTrades.length ? 100 - allocCryptoPct : 0;

      const totalPnlPct = totalWalletBalance > 0 ? (totalPnl / totalWalletBalance) * 100 : 0;

      const computedSnapshot = {
        total_value:      totalWalletBalance,
        daily_pnl:        0,
        total_pnl:        totalPnl,
        total_pnl_pct:    totalPnlPct,
        open_positions:   openPositionsCount,
        win_rate_pct:     winRatePct,
        alloc_crypto_pct: allocCryptoPct,
        alloc_forex_pct:  allocForexPct,
      };

      const enrichedUser = {
        ...(profile ?? {
          first_name: session.user.user_metadata?.first_name ?? '',
          last_name: '',
          email: session.user.email,
          plan: 'free',
          is_verified: false,
        }),
        currency,
        currSymbol,
        _portfolioVal: fmtCurr(totalWalletBalance),
        _dailyChange:  '',
      };

      setUser(enrichedUser);
      setSnapshot(computedSnapshot);
      if (bars?.length)      setPerfBars(bars);
      if (openTrades.length) setPositions(openTrades);
      if (openStockPositions.length) setStockPositions(openStockPositions);
      if (copies?.length) {
        setCopyRels(copies.map(r => ({
          ...r,
          trader_profiles: r.trader_profiles ? {
            ...r.trader_profiles,
            trader_performance: (r.trader_profiles.trader_performance ?? [])
              .sort((a,b) => (b.period === '1Y' ? 1 : -1)).slice(0,1),
          } : null,
        })));
      }
      if (mp?.length) setMpItems(mp);
      // Apply appearance from DB settings (and sync to localStorage for other pages)
      if (userSettings) {
        localStorage.setItem('tf_appearance', JSON.stringify(userSettings));
        applyAppearance(userSettings);
      } else {
        // Fall back to whatever is already in localStorage
        applyAppearance();
      }
      setAuthChecked(true);
    };

    supabase.auth.getSession().then(({ data: { session } }) => bootstrap(session));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) window.location.href = '/login';
    });

    return () => { mounted = false; subscription.unsubscribe(); };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/login';
  };

  useEffect(() => {
    if (!sidebarOpen) return;
    const close = () => setSidebarOpen(false);
    document.addEventListener('keydown', e => e.key === 'Escape' && close());
    return () => document.removeEventListener('keydown', close);
  }, [sidebarOpen]);

  if (!authChecked) {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />
        <div className="mesh-bg" aria-hidden="true">
          <span className="mesh-blob mesh-blob-a" />
          <span className="mesh-blob mesh-blob-b" />
          <span className="mesh-blob mesh-blob-c" />
        </div>
        <div style={{ height:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:18, background:'var(--bg)' }}>
          <div style={{ display:'flex', alignItems:'flex-end', gap:5, height:32 }}>
            {[0,1,2,3,4].map(i => (
              <span key={i} style={{
                display:'inline-block',
                width:5, height:8, borderRadius:2,
                background:'var(--accent)',
                boxShadow:'0 0 10px -1px var(--accent-glow)',
                animation:`loaderBar 1s ease-in-out ${i * 0.11}s infinite`,
              }} />
            ))}
          </div>
          <span style={{ fontFamily:'var(--mono)', fontSize:12, color:'var(--muted)', letterSpacing:'.06em' }}>Loading dashboard…</span>
          <style>{`@keyframes loaderBar { 0%, 100% { height: 8px; opacity: .45; } 50% { height: 32px; opacity: 1; } }`}</style>
        </div>
      </>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

      <div className="mesh-bg" aria-hidden="true">
        <span className="mesh-blob mesh-blob-a" />
        <span className="mesh-blob mesh-blob-b" />
        <span className="mesh-blob mesh-blob-c" />
      </div>

      <div className={`sb-backdrop${sidebarOpen ? ' open' : ''}`} onClick={() => setSidebarOpen(false)} />

      <div className={`shell${railExpanded ? ' rail-expanded' : ''}`}>
        <Sidebar
          open={sidebarOpen}
          expanded={railExpanded}
          onToggleExpand={() => setRailExpanded(v => !v)}
          onClose={() => setSidebarOpen(false)}
          user={user}
          onLogout={handleLogout}
        />

        <div className="right-panel">
          <Topbar onMenu={() => setSidebarOpen(v => !v)} user={user} onLogout={handleLogout} railExpanded={railExpanded} onToggleRail={() => setRailExpanded(v => !v)} />
          <Ticker />

          <main className="main">
            <div className="page-header">
              <div>
                <div className="page-greeting">Dashboard</div>
                <div className="page-sub">
                  <span className="live-dot" />
                  Markets up 2.3% today · Last updated just now
                </div>
              </div>
              <a href="/payments">
                <button type="button" className="btn btn-accent btn-md">
                  <Plus size={15} /> Deposit
                </button>
              </a>
            </div>

            <div className="bento">
              <div className="cell-2"><HeroCard snapshot={snapshot} currSymbol={user?.currSymbol ?? '$'} /></div>
              <div className="cell-2"><StatCluster snapshot={snapshot} currSymbol={user?.currSymbol ?? '$'} /></div>

              <div className="cell-2"><PerformanceCard activeBar={activeBar} onBar={setActiveBar} perfBars={perfBars} snapshot={snapshot} /></div>
              <div className="cell-2"><AllocationCard snapshot={snapshot} /></div>

              <div className="cell-4"><PositionsCard positions={positions} currSymbol={user?.currSymbol ?? '$'} /></div>

              <div className="cell-2"><StockPositionsCard positions={stockPositions} currSymbol={user?.currSymbol ?? '$'} /></div>
              <div className="cell-2"><MarketplaceCard mpItems={mpItems} /></div>

              <div className="cell-2"><QuickActionsCard /></div>
              <div className="cell-2"><PlanCard plan={user?.plan} /></div>
            </div>
          </main>
        </div>
      </div>

      <nav className="bottom-nav">
        <a href="/dashboard" className="bn-item active"><LayoutDashboard />Home</a>
        <a href="/terminal" className="bn-item"><CandlestickChart />Trade</a>
        <a href="/market-place" className="bn-item"><Bot />Bots</a>
        <a href="/copy-trading" className="bn-item"><Copy />Copy</a>
        <a href="/profile" className="bn-item"><UserCircle />Profile</a>
      </nav>
    </>
  );
}